/**
 * Abbreviation-list service for the AETHER frontend.
 *
 * Talks to the Cognito-protected `/abbreviations` API. Reads are available to
 * any authenticated user; create/update/delete require MXO-L6 membership and
 * are enforced server-side (a non-member receives a 403).
 */
import { createLogger } from '../utils/logger';
import { authedFetch } from './apiClient';
import { setAbbreviations } from '../data/abbreviationStore';

const logger = createLogger('AbbreviationService');

/** A single abbreviation and the text it expands to. */
export interface Abbreviation {
  abbreviation: string;
  expansion: string;
  standalone: boolean;
}

/** Result of a create/update/delete mutation. */
export interface AbbreviationMutationResult {
  ok: boolean;
  status: number;
  message?: string;
}

const NETWORK_ERROR: AbbreviationMutationResult = {
  ok: false,
  status: 0,
  message: 'Network error — please try again.',
};

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

/**
 * Fetches the full abbreviation list. Returns `null` on request failure so
 * callers can distinguish a failure from an empty list.
 */
export async function fetchAbbreviations(): Promise<Abbreviation[] | null> {
  try {
    const response = await authedFetch('/abbreviations');
    if (!response || !response.ok) {
      if (response) logger.error('Failed to fetch abbreviations', { status: response.status });
      return null;
    }
    const data = await response.json();
    return Array.isArray(data.abbreviations) ? data.abbreviations : [];
  } catch (err: unknown) {
    logger.error('fetchAbbreviations failed', { error: errorMessage(err) });
    return null;
  }
}

/**
 * Refreshes the in-memory abbreviation store from the API so note expansion and
 * casing reflect the latest admin edits. Called after login and after a
 * successful create/update/delete. A failed fetch (null) leaves the current
 * store — the static seed or last good copy — untouched. Never rejects, so
 * callers may invoke it fire-and-forget without an unhandled rejection.
 */
export async function refreshAbbreviationStore(): Promise<void> {
  try {
    const list = await fetchAbbreviations();
    if (list !== null) setAbbreviations(list);
  } catch (err: unknown) {
    logger.error('refreshAbbreviationStore failed', { error: errorMessage(err) });
  }
}

/** Normalizes a fetch Response (or null) into a mutation result. */
async function toMutationResult(response: Response | null): Promise<AbbreviationMutationResult> {
  if (!response) return NETWORK_ERROR;
  if (response.ok) return { ok: true, status: response.status };
  let message = `Request failed (${response.status}).`;
  try {
    const body = await response.json();
    if (body && typeof body.message === 'string') message = body.message;
  } catch {
    message = `Request failed (${response.status}).`;
  }
  return { ok: false, status: response.status, message };
}

/** Creates a new abbreviation. A 409 indicates it already exists. */
export async function createAbbreviation(input: Abbreviation): Promise<AbbreviationMutationResult> {
  try {
    const response = await authedFetch('/abbreviations', {
      method: 'POST',
      body: JSON.stringify(input),
      includeContentType: true,
    });
    return await toMutationResult(response);
  } catch (err: unknown) {
    logger.error('createAbbreviation failed', { error: errorMessage(err) });
    return NETWORK_ERROR;
  }
}

/** Updates an existing abbreviation's expansion and standalone flag. */
export async function updateAbbreviation(
  abbreviation: string,
  input: { expansion: string; standalone: boolean },
): Promise<AbbreviationMutationResult> {
  try {
    const response = await authedFetch(`/abbreviations/${encodeURIComponent(abbreviation)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
      includeContentType: true,
    });
    return await toMutationResult(response);
  } catch (err: unknown) {
    logger.error('updateAbbreviation failed', { error: errorMessage(err) });
    return NETWORK_ERROR;
  }
}

/** Deletes an abbreviation. */
export async function deleteAbbreviation(
  abbreviation: string,
): Promise<AbbreviationMutationResult> {
  try {
    const response = await authedFetch(`/abbreviations/${encodeURIComponent(abbreviation)}`, {
      method: 'DELETE',
    });
    return await toMutationResult(response);
  } catch (err: unknown) {
    logger.error('deleteAbbreviation failed', { error: errorMessage(err) });
    return NETWORK_ERROR;
  }
}
